"use client"

import { ArrowRight, Briefcase, FileText } from "lucide-react"
import Link from "next/link"

import type { RelatedLink } from "@/types/post"

interface RelatedContentProps {
    relatedServices?: RelatedLink[]
    relatedWork?: RelatedLink[]
    className?: string
}

export function RelatedContent({ relatedServices, relatedWork, className = "" }: RelatedContentProps) {
    const hasServices = relatedServices && relatedServices.length > 0
    const hasWork = relatedWork && relatedWork.length > 0

    if (!hasServices && !hasWork) {
        return null
    }

    return (
        <div className={`mt-16 pt-8 border-t border-white/10 ${className}`}>
            <h3 className="text-lg font-semibold text-white mb-6">Related Resources</h3>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Related Services */}
                {hasServices && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
                            <Briefcase className="w-4 h-4" />
                            <span>Explore Our Services</span>
                        </div>
                        <div className="space-y-2">
                            {relatedServices.map((service) => (
                                <Link
                                    key={service.href}
                                    href={service.href}
                                    className="group flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/20 transition-all"
                                >
                                    <span className="text-white group-hover:text-premium-cyan transition-colors">
                                        {service.label}
                                    </span>
                                    <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-premium-cyan group-hover:translate-x-1 transition-all" />
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {/* Related Work */}
                {hasWork && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
                            <FileText className="w-4 h-4" />
                            <span>See It In Action</span>
                        </div>
                        <div className="space-y-2">
                            {relatedWork.map((work) => (
                                <Link
                                    key={work.href}
                                    href={work.href}
                                    className="group flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/20 transition-all"
                                >
                                    <span className="text-white group-hover:text-premium-violet transition-colors">
                                        {work.label}
                                    </span>
                                    <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-premium-violet group-hover:translate-x-1 transition-all" />
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* CTA */}
            <div className="mt-8 text-center">
                <Link
                    href="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-premium-cyan to-premium-violet text-white font-medium rounded-full hover:opacity-90 transition-opacity"
                >
                    Start a Conversation
                    <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
        </div>
    )
}
